import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import BrandButton from "../../../components/v2/Buttons/BrandButton";
import { validatePhoneNumber } from "../../../components/validation/validatePhoneNumber";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../../context/CreateStoreContext";
import useComponentSize from "../../../hooks/useComponentSize";

const { Title, Paragraph } = Typography;

export default function ContactInfo({ proceed, back }) {
  const componentSize = useComponentSize();
  const dispatch = useStoreDispatch();
  const { contact } = useStore();
  const [form] = Form.useForm();
  const [isSecondaryHasValue, setIsSecondaryHasValue] = useState(false);
  const submitButtonRef = useRef();
  const watchlegalPhoneNumber = Form.useWatch("legalPhoneNumber", form);
  const watchsecondaryFirstName = Form.useWatch("secondaryFirstName", form);
  const watchsecondaryLastName = Form.useWatch("secondaryLastName", form);
  const watchsecondaryEmailAddress = Form.useWatch(
    "secondaryEmailAddress",
    form,
  );
  const watchsecondaryPhoneNumber = Form.useWatch("secondaryPhoneNumber", form);
  const watchsagreeSecondary = Form.useWatch("agreeSecondary", form);

  const onFinish = (values) => {
    dispatch({ type: ACTIONTYPES.CONTACT, data: values });
    proceed();
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    const shouldValidate = Boolean(
      watchsecondaryFirstName ||
        watchsecondaryLastName ||
        watchsecondaryEmailAddress ||
        watchsecondaryPhoneNumber ||
        watchsagreeSecondary,
    );
    setIsSecondaryHasValue(shouldValidate);
    // shouldValidate &&
    form.validateFields([
      "secondaryFirstName",
      "secondaryLastName",
      "secondaryEmailAddress",
      "secondaryPhoneNumber",
      "agreeSecondary",
    ]);
  }, [
    watchsecondaryFirstName,
    watchsecondaryLastName,
    watchsecondaryEmailAddress,
    watchsecondaryPhoneNumber,
    watchsagreeSecondary,
  ]);

  return (
    <Row className="BevzForm">
      <Col
        xs={{ span: 22, offset: 1 }}
        lg={{ span: 20, offset: 2 }}
        xl={{ span: 15, offset: 2 }}
      >
        <Title
          level={componentSize === "default" ? 4 : 2}
          className="BevzFormHeader"
        >
          Contact Information
        </Title>
        <Paragraph className="BevzFormSubHeader">
          Make sure to provide correct details about your contact.
        </Paragraph>

        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          initialValues={contact}
          requiredMark={false}
          style={{ marginTop: "2.5rem", width: "100%" }}
        >
          <Row gutter={30}>
            <Col xs={24} md={12}>
              <OwnerTag />
              <Form.Item
                name="legalFirstName"
                label={
                  <>
                    Legal First Name<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                ]}
              >
                <Input placeholder="" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <OwnerTag />
              <Form.Item
                name="legalLastName"
                label={
                  <>
                    Legal Last Name<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                ]}
              >
                <Input placeholder="" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={30}>
            <Col xs={24} md={12}>
              <OwnerTag />
              <Form.Item
                name="legalEmailAddress"
                label={
                  <>
                    Email Address<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    type: "email",
                    message: "Please enter valid email address.",
                  },
                ]}
              >
                <Input placeholder="owner@email.com" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <OwnerTag />
              <Form.Item
                name="legalPhoneNumber"
                label={
                  <>
                    Phone Number<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      const res =
                        value.length >= 4 && validatePhoneNumber(value);

                      if (res.isValid === false) {
                        return Promise.reject(new Error(res.error));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(value) => {
                  if (!value) return value;
                  const cleaned = value.replace(/[^0-9\s\-\+\(\)]/g, "");
                  const res = validatePhoneNumber(cleaned);
                  return res.isValid ? res.formatted : cleaned;
                }}
              >
                <Input
                  placeholder="(000) 000-0000"
                  size="large"
                  maxLength={25}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="agreeOwner"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "You must agree to the full terms and privacy policy.",
                        ),
                      ),
              },
            ]}
          >
            <Checkbox
              onChange={() => {
                form.validateFields(["agreeOwner"]);
              }}
              className="BEVZ_Form"
            >
              By signing up for texts, you agree to receive communications and
              messages about your service from Bevz. Message and data rates may
              apply. See our{" "}
              <a href="https://www.bevz.com/terms-of-service" target="_blank">
                full terms
              </a>{" "}
              and{" "}
              <a href="https://www.bevz.com/privacy-policy" target="_blank">
                privacy policy
              </a>
              .<span style={{ color: "red" }}>*</span>
            </Checkbox>
          </Form.Item>

          <Divider />

          <Row gutter={30}>
            <Col xs={24} md={12}>
              <SecondaryTag />
              <Form.Item
                name="secondaryFirstName"
                label={
                  <>
                    First Name
                    {isSecondaryHasValue ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : null}
                  </>
                }
                required={isSecondaryHasValue}
                rules={[
                  {
                    required: isSecondaryHasValue,
                    message: "This is a required field.",
                  },
                ]}
              >
                <Input placeholder="" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <SecondaryTag />
              <Form.Item
                name="secondaryLastName"
                label={
                  <>
                    Last Name
                    {isSecondaryHasValue ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : null}
                  </>
                }
                required={isSecondaryHasValue}
                rules={[
                  {
                    required: isSecondaryHasValue,
                    message: "This is a required field.",
                  },
                ]}
              >
                <Input placeholder="" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={30}>
            <Col xs={24} md={12}>
              <SecondaryTag />
              <Form.Item
                name="secondaryEmailAddress"
                label={
                  <>
                    Email Address
                    {isSecondaryHasValue ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : null}
                  </>
                }
                required={isSecondaryHasValue}
                rules={[
                  {
                    required: isSecondaryHasValue,
                    message: "This is a required field.",
                  },
                  {
                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Please enter valid email address.",
                  },
                ]}
              >
                <Input placeholder="secondary@email.com" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <SecondaryTag />
              <Form.Item
                name="secondaryPhoneNumber"
                label={
                  <>
                    Phone Number
                    {isSecondaryHasValue ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : null}
                  </>
                }
                required={isSecondaryHasValue}
                rules={[
                  {
                    required: isSecondaryHasValue,
                    message: "This is a required field.",
                  },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      const res =
                        value.length >= 4 && validatePhoneNumber(value);

                      if (res.isValid === false) {
                        return Promise.reject(new Error(res.error));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(value) => {
                  if (!value) return value;
                  const cleaned = value.replace(/[^0-9\s\-\+\(\)]/g, "");
                  const res = validatePhoneNumber(cleaned);
                  return res.isValid ? res.formatted : cleaned;
                }}
              >
                <Input
                  placeholder="(000) 000-0000"
                  size="large"
                  maxLength={25}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="agreeSecondary"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value || !isSecondaryHasValue
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "You must agree to the full terms and privacy policy.",
                        ),
                      ),
              },
            ]}
          >
            <Checkbox
              onChange={() => {
                form.validateFields(["agreeSecondary"]);
              }}
              className="BEVZ_Form"
            >
              By signing up for texts, you agree to receive communications and
              messages about your service from Bevz. Message and data rates may
              apply. See our{" "}
              <a href="https://www.bevz.com/terms-of-service" target="_blank">
                full terms
              </a>{" "}
              and{" "}
              <a href="https://www.bevz.com/privacy-policy" target="_blank">
                privacy policy
              </a>
              .{" "}
              {isSecondaryHasValue ? (
                <span style={{ color: "red" }}>*</span>
              ) : null}
            </Checkbox>
          </Form.Item>

          <Row
            align="middle"
            justify="space-between"
            className="BevzFormButtonSection"
          >
            <Col>
              <Form.Item>
                <Button
                  type="default"
                  size="large"
                  shape="round"
                  icon={<ArrowLeftOutlined />}
                  onClick={back}
                >
                  Back
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <BrandButton
                  type="solidyellow"
                  ref={submitButtonRef}
                  htmlType="submit"
                >
                  {componentSize === "default"
                    ? "Proceed"
                    : "Proceed to Business Information"}
                </BrandButton>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
}

const OwnerTag = () => <span className="BevzFormLabelTag">OWNER'S</span>;
const SecondaryTag = () => (
  <span className="BevzFormLabelTag">SECONDARY CONTACT'S</span>
);
